import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import Table from '../../../components/common/Table/Table'
import Loader from '../../../components/common/Loader'
import ValidationBanner from '../../../components/common/ValidationBanner'
import Print from '../../../../public/assets/images/printer-outline.svg'
import exportIcon from '../../../../public/assets/images/download-outline.svg'
import calendarIcon from '../../../../public/assets/images/calendar-dates-outline.svg'
import listIcon from '../../../../public/assets/images/list-outline.svg'
import eyeIcon from '../../../../public/assets/images/eye-frame-outline.svg'
import { usePrint } from '../../../hooks/usePrint'
import { useProfileDisplay } from '../../../hooks/useProfileDisplay'
import { usePaginationTracking } from '../../../hooks/usePaginationTracking'
import { transformRequestsToTableRows, DEFAULT_REQUEST_COLUMNS } from '../../../utils/tabDataTransform.tsx'
import plusIcon from '../../../../public/assets/images/plus-circle-outline.svg'
import CalendarWeekView from '../../../components/common/CalendarWeekView/CalendarWeekView'
import FilterComponent, { FilterData } from '../../../components/common/FilterComponent/FilterComponent'
import minusIcon from '../../../../public/assets/images/minus-circle-solid.svg'
import CalendarStatusInfo from '../../../components/common/CalendarStatusInfo';
import LabeledValue from '../../../components/common/LabeledValue'
import { getAllRequests, setCurrentPage, setSelectedRequest, setTimeOffRequestsSortOrder } from '../../../store/slices/Requestor/employeeInfoSlice'
import { getCalendarFilters, mapCalendarFiltersResponse, CalendarViewFilters, CalendarRequestStatus, CalendarRequestType, CheckboxFiltersConfig } from '../../../store/slices/Requestor/calendarViewSlice'
import { setTimeOffRequestsCount } from '../../../store/slices/Requestor/requestorTabCountSlice'
import { RootState, AppDispatch } from '../../../store/store'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { getTimeOffTypeCode } from '../../../utils/timeOffTypes'
import { getStatusCode } from '../../../utils/requestStatus'
import { ALL_STATUS_CODES } from '../../../constants/RequestStatus'
import { exportRequestorRequests } from '../../../store/slices/Requestor/requestorExportSlice.ts'

/**
 * Extract code from a "code - description" formatted label.
 * E.g., "00053997 - Camille Bauer" -> "00053997"
 * If no separator found, returns the original value.
 */
const extractCodeFromLabel = (label: string): string => {
  const parts = label.split(' - ')
  return parts[0]?.trim() || label
}

/**
 * Extract code from a "name - code" formatted label (used for approval group).
 * E.g., "Camille Bauer - 00053997" -> "00053997"
 * If no separator found, returns the original value.
 */
const extractCodeFromEndOfLabel = (label: string): string => {
  const parts = label.split(' - ')
  return parts.length > 1 ? parts.at(-1)?.trim() || label : label
}

/**
 * Convert FilterData from FilterComponent into CalendarViewFilters for the API.
 */
const mapFilterDataToApiFilters = (
  filterData: FilterData | null,
  dateMode: 'all' | 'range'
): CalendarViewFilters | undefined => {
  if (!filterData) return undefined

  const checkboxFilters = filterData.checkboxFilters || {}

  // Extract selected status labels and map to codes using shared utility
  const selectedStatuses = (checkboxFilters.statusFilter || [])
    .filter((label) => !/^all/i.test(label))
    .map((label) => getStatusCode(label))
    .filter((code): code is CalendarRequestStatus => Boolean(code))

  // Extract selected request type labels and map to codes using utility
  const selectedTypes = (checkboxFilters.requestTypeFilter || [])
    .filter((label) => !/^all/i.test(label))
    .map((label) => getTimeOffTypeCode(label))
    .filter((code): code is CalendarRequestType => Boolean(code))

  // Default request type codes when none selected
  const ALL_REQUEST_TYPE_CODES: CalendarRequestType[] = ['V', 'F', 'D', 'B', 'A', 'O']

  // If no specific selections, use all values
  const statusArray: CalendarRequestStatus[] = selectedStatuses.length > 0
    ? selectedStatuses
    : ALL_STATUS_CODES

  const requestTypeArray: CalendarRequestType[] = selectedTypes.length > 0
    ? selectedTypes
    : ALL_REQUEST_TYPE_CODES

  // Build the filters object
  const filters: CalendarViewFilters = {
    dateOption: dateMode === 'range' ? 'R' : 'A',
    status: statusArray,
    requestType: requestTypeArray,
  }

  // Add date range if applicable
  if (dateMode === 'range' && filterData.startDate && filterData.endDate) {
    // Convert MM/DD/YYYY to YYYY-MM-DD
    const convertDate = (dateStr: string): string => {
      const [mm, dd, yyyy] = dateStr.split('/')
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`
    }
    filters.startDate = convertDate(filterData.startDate)
    filters.endDate = convertDate(filterData.endDate)
  }

  // Add optional filters
  // Don't send field if "All" is checked - only send when specific items are selected
  // API accepts string[] for these fields
  const getFilterValueOrAll = (arr?: string[]): string[] | undefined => {
    if (!arr || arr.length === 0) return undefined
    // If "All" is present in the array, don't send this field at all
    const hasAll = arr.some((v) => /^all/i.test(v))
    if (hasAll) return undefined
    // Return unique codes as array
    return [...new Set(arr.map(extractCodeFromLabel))]
  }

  // For primaryApprover: unique codes as array, never send "all"
  const getCodeOnlyValue = (arr?: string[]): string[] | undefined => {
    const filtered = (arr || []).filter((v) => !/^all/i.test(v))
    if (filtered.length === 0) return undefined
    return [...new Set(filtered.map(extractCodeFromLabel))]
  }

  // For approvalGroup: labels are "Name - Code" format, extract unique codes from end
  const getApprovalGroupCode = (arr?: string[]): string[] | undefined => {
    const filtered = (arr || []).filter((v) => !/^all/i.test(v))
    if (filtered.length === 0) return undefined
    return [...new Set(filtered.map(extractCodeFromEndOfLabel))]
  }

  const department = getFilterValueOrAll(checkboxFilters.departmentFilter)
  if (department) filters.department = department

  // Union filter: pass full labels as array to allow API to differentiate
  // unions that share the same code (e.g. "NUE - NUE NW Exempt" vs "NUE - NUE PO Program Office")
  const unionArr = checkboxFilters.unionAffiliationFilter
  const unionHasAll = (unionArr ?? []).some((v) => /^all/i.test(v))
  const unionFiltered = (unionArr ?? []).filter((v) => !/^all/i.test(v))
  const unionValue = unionHasAll || unionFiltered.length === 0 ? undefined : unionFiltered
  if (unionValue) filters.union = unionValue
  
  const jobCode = getFilterValueOrAll(checkboxFilters.jobCodeFilter)
  if (jobCode) filters.jobCode = jobCode
  
  const approvalGroup = getApprovalGroupCode(checkboxFilters.approvalGroupFilter)
  if (approvalGroup) filters.approvalGroup = approvalGroup
  
  const primaryApprover = getCodeOnlyValue(checkboxFilters.primaryApproverFilter)
  if (primaryApprover) filters.primaryApprover = primaryApprover

  return filters
}

interface TimeOffRequestsProps {
  // No props needed - uses Redux for state management
}

type RequestSortColumn = 'fromDate' | 'hoursRequested'

interface RenderListViewProps {
  requests: any[]
  columns: any[]
  rows: any[]
  loading: boolean
  totalRecords: number
  pageSize: number
  currentPage: number
  handlePageChange: (page: number) => void
  error?: string | null
  sortColumn?: string
  sortDirection?: 'ASC' | 'DESC'
  onSort?: (columnKey: string, sortDirection: 'ASC' | 'DESC') => void
}

const renderFilterSummary = (appliedFilter: FilterData | null, dateMode: 'all' | 'range') => {
  if (!appliedFilter) return null

  const checkboxFilters = appliedFilter.checkboxFilters || {}
  const hasSelection = (arr?: string[]) => {
    if (!arr || arr.length === 0) return false
    return !arr.some((v) => /all/i.test(v))
  }

  const selectedHeaders: string[] = []

  // Add Date Range if dateMode is 'range' and dates are present
  if (dateMode === 'range' && (appliedFilter.startDate || appliedFilter.endDate)) {
    selectedHeaders.push('Date Range')
  }

  // Add other filters
  const filterLabels = [
    [checkboxFilters.departmentFilter, 'Department'],
    [checkboxFilters.unionAffiliationFilter, 'Union'],
    [checkboxFilters.approvalGroupFilter, 'Approver'],
    [checkboxFilters.jobCodeFilter, 'Job Code'],
    [checkboxFilters.statusFilter, 'Request Status'],
    [checkboxFilters.requestTypeFilter, 'Request Type'],
  ]
    .filter(([filter]) => hasSelection(filter as string[]))
    .map(([, label]) => label as string)

  selectedHeaders.push(...filterLabels)

  if (selectedHeaders.length === 0) return null

  return (
    <div className="mb-8! p-6! rounded" style={{ background: '#FEF0E7' }}>
      <LabeledValue label='Filters Applied' value={selectedHeaders.join(', ')} />
    </div>
  )
}

const renderListView = (props: RenderListViewProps) => {
  const { requests, columns, rows, loading, totalRecords, pageSize, currentPage, handlePageChange, error, sortColumn, sortDirection, onSort } = props
  
  const renderContent = () => {
    if (error) {
      return (
        <div className="flex items-center justify-center min-h-96 py-12">
          <ValidationBanner
            variant="error"
            heading={error}
          />
        </div>
      )
    }
    
    if (requests.length === 0 && !loading) {
      return (
        <div className="flex items-center justify-center min-h-96 py-12">
          <div className="text-center">
            <p className="p3 -medium">There are no current Time Off Requests</p>
          </div>
        </div>
      )
    }
    
    return (
      <Table
        columns={columns}
        rows={rows}
        showPagination={true}
        paginationProps={{ totalItems: totalRecords, itemsPerPage: pageSize }}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onSort={onSort}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
      />
    )
  }
  
  return (
  <>
    {loading && <Loader />}
    <div className="relative">
      {renderContent()}
    </div>
  </>
)
}

/**
 * Validates calendar filter data and returns an error message if invalid, or null if valid.
 */
const validateCalendarFilter = (
  filterData: FilterData,
  dateFilterMode: 'all' | 'range'
): string | null => {
  // Validate date range when dateFilterMode is 'range'
  if (dateFilterMode === 'range') {
    if (!filterData.startDate && !filterData.endDate) return 'Start Date and End Date are required.'
    if (!filterData.startDate) return 'Start Date is required.'
    if (!filterData.endDate) return 'End Date is required.'
  }

  // Validate at least one request type is selected
  const selectedRequestTypes = (filterData.checkboxFilters?.requestTypeFilter || [])
    .filter((label) => !/^all(\s|$)/i.test(label))
  if (selectedRequestTypes.length === 0) return 'Please select at least one Request Type.'

  // Validate at least one status is selected
  const selectedStatuses = (filterData.checkboxFilters?.statusFilter || [])
    .filter((label) => !/^all(\s|$)/i.test(label))
  if (selectedStatuses.length === 0) return 'Please select at least one Request Status.'

  return null
}

/**
 * Creates the initial all-selected filter from the calendar filter config.
 * @param filtersConfig - Filter groups returned by the calendar filters API.
 * @returns The initial filter data, or undefined when no config is available.
 */
const createDefaultInitialFilter = (
  filtersConfig: CheckboxFiltersConfig | undefined
): FilterData | undefined => {
  if (!filtersConfig) return undefined

  const checkboxFilters: Record<string, string[]> = {}
  Object.keys(filtersConfig).forEach((filterKey) => {
    const group = filtersConfig[filterKey]
    if (group?.labels && group.labels.length > 0) {
      checkboxFilters[filterKey] = [...group.labels]
    }
  })

  return { startDate: '', endDate: '', checkboxFilters }
}

type RequestView = 'list' | 'calendar'

interface ViewToggleStyles {
  className: string
  color: string
  filter: string
  labelClassName: string
}

/**
 * Returns the visual state for a list or calendar view toggle.
 * @param view - Currently selected request view.
 * @param target - View represented by the toggle.
 * @returns Styles that preserve the selected and unselected states.
 */
const getViewToggleStyles = (view: RequestView, target: RequestView): ViewToggleStyles => {
  const isSelected = view === target
  return {
    className: `${isSelected ? 'p3 -medium inky' : 'p3 -book'} inline-flex items-center gap-2 ${isSelected ? 'opacity-100' : 'opacity-80 hover:opacity-100'} ${isSelected ? 'cursor-default' : 'cursor-pointer'}`,
    color: isSelected ? '#0D1C3D' : '#0074AD',
    filter: isSelected ? 'grayscale(100%) brightness(70%)' : 'none',
    labelClassName: isSelected ? '' : 'underline hover:no-underline',
  }
}

/**
 * Changes the request view only when the target is not already selected.
 * @param view - Currently selected request view.
 * @param target - Requested view.
 * @param setView - React state setter for the request view.
 */
const changeRequestView = (
  view: RequestView,
  target: RequestView,
  setView: React.Dispatch<React.SetStateAction<RequestView>>
): void => {
  if (view !== target) setView(target)
}

interface CalendarViewContentProps {
  isFilterOpen: boolean
  appliedFilter: FilterData | null
  dateFilterMode: 'all' | 'range'
  dateRangeError: string | null
  dateRangeErrorRef: React.RefObject<HTMLDivElement>
  defaultInitialFilter: FilterData | undefined
  filtersConfig: CheckboxFiltersConfig
  calendarApiFilters: CalendarViewFilters | undefined
  onToggleFilter: () => void
  onClearFilter: () => void
  onDateModeChange: (mode: 'all' | 'range') => void
  onDateRangeErrorChange: (error: string | null) => void
  onFilterChange: (filter: FilterData | null) => void
  onFilterOpenChange: (isOpen: boolean) => void
  onWeeklyRequestCountChange: (count: number) => void
  onCalendarDataLoaded: () => void
}

/**
 * Renders calendar controls, filters, and the weekly request view.
 * @param props - Calendar view state and event handlers.
 * @returns The calendar view content.
 */
const CalendarViewContent: React.FC<CalendarViewContentProps> = ({
  isFilterOpen,
  appliedFilter,
  dateFilterMode,
  dateRangeError,
  dateRangeErrorRef,
  defaultInitialFilter,
  filtersConfig,
  calendarApiFilters,
  onToggleFilter,
  onClearFilter,
  onDateModeChange,
  onDateRangeErrorChange,
  onFilterChange,
  onFilterOpenChange,
  onWeeklyRequestCountChange,
  onCalendarDataLoaded,
}) => {
  const handleApplyFilter = (filterData: FilterData) => {
    const validationError = validateCalendarFilter(filterData, dateFilterMode)
    if (validationError) {
      onDateRangeErrorChange(validationError)
      setTimeout(() => dateRangeErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100)
      return
    }

    onDateRangeErrorChange(null)
    onFilterChange(filterData)
    onFilterOpenChange(false)
  }

  const initialFilter = appliedFilter && defaultInitialFilter
    ? {
        ...appliedFilter,
        checkboxFilters: Object.fromEntries(
          Object.keys(defaultInitialFilter.checkboxFilters ?? {}).map((key) => {
            const applied = appliedFilter.checkboxFilters?.[key] ?? []
            const defaults = defaultInitialFilter.checkboxFilters?.[key] ?? []
            return [key, applied.length === 0 ? defaults : applied]
          })
        ),
      }
    : defaultInitialFilter

  return (
    <>
      <div className="flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-4 md:gap-0 mt-4! mb-4 px-4 md:px-1 py-2">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-start gap-4 md:gap-8 w-full md:w-auto">
          {!isFilterOpen && (
            <button
              type="button"
              className={`flex items-center gap-2 px-4 py-1 w-full md:w-auto justify-start ${appliedFilter ? 'hover:opacity-80 cursor-pointer' : 'cursor-not-allowed'}`}
              disabled={!appliedFilter}
              onClick={onClearFilter}
            >
              <img
                src={eyeIcon}
                alt="Show All Requests"
                className="inline-block w-5 h-5"
                style={appliedFilter ? undefined : { filter: 'brightness(0)' }}
              />
              <span className={`p2 -medium ${appliedFilter ? 'text-[#0074AD]' : 'text-[#0D1C3D]'}`}>
                Show All Requests
              </span>
            </button>
          )}

          <button
            type="button"
            className="flex items-center gap-2 px-4 py-1 w-full md:w-auto justify-start"
            onClick={onToggleFilter}
          >
            <img
              src={isFilterOpen ? minusIcon : plusIcon}
              alt={isFilterOpen ? 'Minus Icon' : 'Plus Icon'}
              className="inline-block shrink-0"
            />
            <span className="p2 -medium text-[#003B71]">Filter Requests</span>
          </button>
        </div>
      </div>
      {isFilterOpen && (
        <div className="mb-4 relative md:left-6">
          <div className="mb-3">
            {dateRangeError && (
              <div ref={dateRangeErrorRef} className="mb-4">
                <ValidationBanner variant="error" heading="Error" message={dateRangeError} />
              </div>
            )}
            <div className="p2 -medium mt-4!">Date</div>
            <div className="flex flex-col items-start gap-2 mt-2">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="dateFilter"
                  value="all"
                  checked={dateFilterMode === 'all'}
                  onChange={() => {
                    onDateModeChange('all')
                    onDateRangeErrorChange(null)
                  }}
                />
                <span className="p2">All</span>
              </label>
              <label className="inline-flex items-center gap-2 mb-4!">
                <input
                  type="radio"
                  name="dateFilter"
                  value="range"
                  checked={dateFilterMode === 'range'}
                  onChange={() => onDateModeChange('range')}
                />
                <span className="p2">Date Range</span>
              </label>
            </div>
          </div>
          <div className="!pb-6">
            <FilterComponent
              onApplyFilter={handleApplyFilter}
              onCancel={() => {
                onDateRangeErrorChange(null)
                onFilterOpenChange(false)
              }}
              onReset={() => {
                onDateRangeErrorChange(null)
                onFilterChange(null)
                onDateModeChange('all')
                onFilterOpenChange(false)
              }}
              initialFilter={initialFilter}
              dateMode={dateFilterMode}
              filtersConfig={filtersConfig}
            />
          </div>
        </div>
      )}

      <hr className="-variation-1" />
      {renderFilterSummary(appliedFilter, dateFilterMode)}
      <div className="w-full flex justify-center md:justify-end mb-6!">
        <CalendarStatusInfo />
      </div>
      <CalendarWeekView
        filters={calendarApiFilters}
        filterStartDate={appliedFilter?.startDate}
        onWeeklyRequestCountChange={(count) => {
          onWeeklyRequestCountChange(count)
          onCalendarDataLoaded()
        }}
      />
    </>
  )
}

/**
 * Renders the requestor time-off requests list and calendar views.
 * Handles pagination, sorting, filtering, export, and print behavior.
 */
const TimeOffRequests: React.FC<TimeOffRequestsProps> = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { requests, currentPage, pageSize, totalRecords, loading, error, sortOrder } = useSelector(
    (state: RootState) => state.employeeInfo.timeOffRequests
  )
  const [activeSortColumn, setActiveSortColumn] = React.useState<string>('dateRequestedSortValue')
  const calendarFiltersData = useSelector(
    (state: RootState) => state.calendarView.filters.data
  )
const exportLoading = useSelector(
  (state: any) => state.requestorExport.loading)
  const [view, setView] = React.useState<'list' | 'calendar'>('list')
  const [isFilterOpen, setIsFilterOpen] = React.useState(false)
  const [appliedFilter, setAppliedFilter] = React.useState<FilterData | null>(null)
  const [dateFilterMode, setDateFilterMode] = React.useState<'all' | 'range'>('all')
  const [dateRangeError, setDateRangeError] = React.useState<string | null>(null)
  const [calendarWeeklyCount, setCalendarWeeklyCount] = React.useState(0)
  /**
   * Track whether the calendar view has received its first data update from
   * the API. This prevents showing a stale count of 0 before the calendar
   * requests have been fetched and filtered by week.
   */
  const [calendarDataLoaded, setCalendarDataLoaded] = React.useState(false)
  const dateRangeErrorRef = React.useRef<HTMLDivElement>(null)
  const { printRef, handlePrint } = usePrint({ fontSize: '9px'})
  const { userInfo, userNuid, primaryApproverInfo } = useProfileDisplay()
  const { mode } = useParams<{ mode?: string }>()
  const isAdminMode = mode === 'admin'
  const manualEmployee = useSelector((state: RootState) => state.manualTimeOff.selectedEmployee)
  const employeeInfoData = useSelector((state: RootState) => state.employeeInfo.employeeInfo.data)
  const printDisplayName = isAdminMode
    ? `${manualEmployee?.name || 'Employee'} (${manualEmployee?.nuid || 'Unknown'})`
    : `${userInfo}${userNuid}`
  const printDisplayApprover = isAdminMode
    ? (employeeInfoData?.primaryApprover || 'NA')
    : primaryApproverInfo
  
  // Print state management
  const [printRows, setPrintRows] = React.useState<any[]>([])
  const [isPrinting, setIsPrinting] = React.useState(false)
  
  // Track pagination changes to prevent redundant API calls
  const { pageChanged, pageSizeChanged, updateRefs } = usePaginationTracking(currentPage, pageSize)

  const getApiSortColumn = React.useCallback(
    (columnKey: string): RequestSortColumn =>
      columnKey === 'hoursRequested' ? 'hoursRequested' : 'fromDate',
    []
  )

  const currentApiSortColumn = React.useMemo(
    () => getApiSortColumn(activeSortColumn),
    [activeSortColumn, getApiSortColumn]
  )

  // Reset calendar data loaded flag when switching away from calendar view
  React.useEffect(() => {
    if (view !== 'calendar') {
      setCalendarDataLoaded(false)
    }
  }, [view])

  // Only fetch when pagination actually changes (not on component remount/tab switch)
  React.useEffect(() => {
    // Skip on initial mount - parent hook handles initial load
    // Only fetch if page or pageSize actually changed
    if (!pageChanged && !pageSizeChanged) {
      return
    }

    updateRefs()

    const now = new Date()
    dispatch(
      getAllRequests({
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate(),
        page: currentPage,
        perPage: pageSize,
        sortOrder,
        sortColumn: currentApiSortColumn,
      })
    )
  }, [dispatch, currentPage, pageSize, pageChanged, pageSizeChanged, updateRefs, sortOrder, currentApiSortColumn])

  // Fetch calendar filter dropdown options once when the calendar view is opened
  React.useEffect(() => {
    if (view === 'calendar' && !calendarFiltersData) {
      const promise = dispatch(getCalendarFilters())
      return () => {
        promise.abort()
      }
    }
  }, [dispatch, view, calendarFiltersData])

  // Map the API filter options into the shape FilterComponent expects
  const filtersConfig = React.useMemo(
    () => mapCalendarFiltersResponse(calendarFiltersData),
    [calendarFiltersData]
  )

  // Create default initial filter with "All" checked for ALL filter sections
  const defaultInitialFilter = React.useMemo(
    () => createDefaultInitialFilter(filtersConfig),
    [filtersConfig]
  )

  // Update tab count when requests change
  // In List View: show total records immediately (API already loaded)
  // In Calendar View: wait for first callback from CalendarWeekView before updating
  //   (ensures we show the actual week-specific count, not a stale 0)
  React.useEffect(() => {
    if (view === 'list') {
      dispatch(setTimeOffRequestsCount(totalRecords))
    } else if (view === 'calendar' && calendarDataLoaded) {
      dispatch(setTimeOffRequestsCount(calendarWeeklyCount))
    }
  }, [dispatch, totalRecords, calendarWeeklyCount, calendarDataLoaded, view])

  const columns = DEFAULT_REQUEST_COLUMNS

  const handleSelectRequest = (request: any) => {
    dispatch(setSelectedRequest(request))
  }

  const rows = transformRequestsToTableRows(requests, handleSelectRequest)
  
  // Handle sorting by calling API with new sortOrder and sortColumn
  const handleSort = (columnKey: string, direction: 'ASC' | 'DESC') => {
    setActiveSortColumn(columnKey)
    dispatch(setTimeOffRequestsSortOrder(direction))
    const apiSortColumn = getApiSortColumn(columnKey)
    const now = new Date()
    dispatch(
      getAllRequests({
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate(),
        page: 1,
        perPage: pageSize,
        sortOrder: direction,
        sortColumn: apiSortColumn,
      })
    )
  }
  
  const isCalendarView = view === 'calendar'

  const toggleFilterOpen = () => {
    setIsFilterOpen((s) => !s)
  }

  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page))
  }

  /**
   * Fetch all records for printing without mutating Redux list pagination state.
   * Uses totalRecords directly for perPage, matching approver pattern.
   */
  const handlePrintWithApi = async () => {
    try {
      setIsPrinting(true)

      const now = new Date()
      const response = await api.get(
        endpoints.requester.getAllTimeOffReqList({
          year: now.getFullYear(),
          month: now.getMonth() + 1,
          day: now.getDate(),
          page: 1,
          perPage: totalRecords,
          sortOrder,
          sortColumn: currentApiSortColumn,
        })
      )

      const items: any[] = Array.isArray(response.data?.items) ? response.data.items : []
      setPrintRows(items)

      // Wait for DOM update before opening print dialog
      setTimeout(() => {
        handlePrint()
        setIsPrinting(false)
      }, 0)
    } catch  {
      setIsPrinting(false)
    }
  }

  const handleDirectPrint = () => {
    setIsPrinting(true)
    handlePrint()
    // Print dialog is blocking in most browsers, so clear after returning.
    setTimeout(() => {
      setIsPrinting(false)
    }, 100)
  }

  // Convert applied UI filters to API format for CalendarWeekView
  const calendarApiFilters = React.useMemo(
    () => mapFilterDataToApiFilters(appliedFilter, dateFilterMode),
    [appliedFilter, dateFilterMode]
  )

  const calendarViewContent = (
    <CalendarViewContent
      isFilterOpen={isFilterOpen}
      appliedFilter={appliedFilter}
      dateFilterMode={dateFilterMode}
      dateRangeError={dateRangeError}
      dateRangeErrorRef={dateRangeErrorRef}
      defaultInitialFilter={defaultInitialFilter}
      filtersConfig={filtersConfig}
      calendarApiFilters={calendarApiFilters}
      onToggleFilter={toggleFilterOpen}
      onClearFilter={() => {
        setAppliedFilter(null)
        setDateFilterMode('all')
        setIsFilterOpen(false)
      }}
      onDateModeChange={setDateFilterMode}
      onDateRangeErrorChange={setDateRangeError}
      onFilterChange={setAppliedFilter}
      onFilterOpenChange={setIsFilterOpen}
      onWeeklyRequestCountChange={setCalendarWeeklyCount}
      onCalendarDataLoaded={() => setCalendarDataLoaded(true)}
    />
  )
  const listToggleStyles = getViewToggleStyles(view, 'list')
  const calendarToggleStyles = getViewToggleStyles(view, 'calendar')

const handleExport = () => {
  const now = new Date()

  dispatch(
    exportRequestorRequests({
      date: now.toISOString().split('T')[0],
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      type: 'TIME_OFF',
      sortOrder,
      sortColumn: currentApiSortColumn,
    })
  )
}
  return (
    <div>
      <style>
        {`
#temp-print-container {
  display: none;
}

.print-only-header {
  display: none;
}

@media print {
  @page {
    size: landscape;
    margin: 10mm;
  }

  .print-only-header {
    display: block !important;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #ccc;
  }

  /* Show print table */
  #temp-print-container {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  #temp-print-container table {
    width: 100% !important;
    border-collapse: collapse !important;
  }

  #temp-print-container th,
  #temp-print-container td {
    border: 1px solid #ddd !important;
    padding: 8px !important;
    text-align: left !important;
  }

  #temp-print-container th {
    background-color: #f5f5f5 !important;
    font-weight: bold !important;
  }
}
        `}
      </style>
      {(exportLoading || isPrinting) && <Loader />}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0 mb-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => changeRequestView(view, 'list', setView)}
              className={listToggleStyles.className}
              aria-pressed={view === 'list'}
              aria-disabled={view === 'list'}
              style={{ color: listToggleStyles.color }}
            >
              <img
                src={listIcon}
                alt="List Icon"
                className="inline-block"
                style={{ filter: listToggleStyles.filter }}
              />
              <span className={listToggleStyles.labelClassName}>List View</span>
            </button>
            <span className="text-gray-400">|</span>
            <button
              type="button"
              onClick={() => changeRequestView(view, 'calendar', setView)}
              className={calendarToggleStyles.className}
              aria-pressed={view === 'calendar'}
              aria-disabled={view === 'calendar'}
              style={{ color: calendarToggleStyles.color }}
            >
              <img
                src={calendarIcon}
                alt="Calendar Icon"
                className="inline-block"
                style={{ filter: calendarToggleStyles.filter }}
              />
              <span className={calendarToggleStyles.labelClassName}>Calendar Week View</span>
            </button>
          </div>
        </div>

        <div className="flex  items-baseline justify-end  gap-2 w-full md:w-auto">
          <button
            type="button"
            className="button -secondary flex items-center w-full md:w-auto" 
            onClick={isCalendarView ? handleDirectPrint : handlePrintWithApi}
            disabled={isPrinting}
          >
            <img src={Print} alt="Print Icon" className="inline-block mr-2" />
            <span>Print</span>
          </button>
                  <button
                type="button"
              className="button -secondary flex items-center w-full md:w-auto"
              onClick={handleExport}
              title="Export File"
            >
              <img
                src={exportIcon}
                alt="Export Icon"
                className="inline-block mr-2"
              />
              <span className="text-sm leading-5">Export File</span>
            </button>
        </div>
      </div>
      <div ref={isCalendarView ? printRef : undefined}>
      {isCalendarView && (
        <div className="print-only-header">
          <p style={{margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '14px'}}>{printDisplayName}</p>
          <p style={{margin: '0 0 4px 0', fontSize: '12px'}}>Primary Approver: {printDisplayApprover}</p>
          <p style={{margin: '0', fontSize: '12px'}}>My Time Off Requests</p>
        </div>
      )}
      {isCalendarView ? (
        calendarViewContent
      ) : (
        renderListView({
          requests,
          columns,
          rows,
          loading,
          totalRecords,
          pageSize,
          currentPage,
          handlePageChange,
          error,
          sortColumn: activeSortColumn,
          sortDirection: sortOrder,
          onSort: handleSort,
        })
      )}
      </div>
      {!isCalendarView && printRows.length > 0 && (
        <div id="temp-print-container" ref={printRef}>
          <div style={{marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #ccc'}}>
            <p style={{margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '14px'}}>{printDisplayName}</p>
            <p style={{margin: '0 0 4px 0', fontSize: '12px'}}>Primary Approver: {printDisplayApprover}</p>
            <p style={{margin: '0', fontSize: '12px'}}>My Time Off Requests</p>
          </div>
          <Table
            columns={columns}
            rows={transformRequestsToTableRows(printRows, handleSelectRequest)}
            isSorting={false}
            showPagination={false}
          />
        </div>
      )}
    </div>
  )
}

export default TimeOffRequests
